import { isEmpty } from 'lodash';
import React from 'react';
import { AtsAsset } from '../../../../../components/ATS/ats-asset';
import { Swimlane } from '../../../../../components/Swimlane/Swimlane';
import { Layout } from '../../../../../queries/ats/layout/types';
import { useContinueWatchingTitles, useMostRecentBookmark } from '../../../../../queries/ats/me/bookmarks/hooks';
import { createCwAsEditorialItems } from '../../../../../queries/ats/me/utils';
import { useRecordings } from '../../../../../queries/ats/recordings/hooks';
import { createKey } from '../../../../../utils/atsLayoutUtils';
import { PatternHandler } from '../../../PatternHandler';
import styles from '../../section-type.module.scss';

interface SectionContinueWatchingProps extends React.HTMLAttributes<HTMLDivElement> {
  view: Layout.SectionView;
}

export function SectionContinueWatching({ view }: SectionContinueWatchingProps) {
  const { data: recordings } = useRecordings();
  const { data = [], isLoading } = useContinueWatchingTitles({
    select: (x) => createCwAsEditorialItems(x, view.style.pattern, recordings),
  });

  if (isLoading) {
    return (
      <Swimlane className={styles.container}>
        <Swimlane.Header>
          <Swimlane.Title>{view.label}</Swimlane.Title>
          <Swimlane.Buttons />
        </Swimlane.Header>
        <Swimlane.Slider>
          {Array.from({ length: 10 }).map((_, index) => (
            <AtsAsset.Skeleton pattern={view.style.pattern} key={index} />
          ))}
        </Swimlane.Slider>
      </Swimlane>
    );
  }

  if (isEmpty(data)) {
    return null;
  }

  return (
    <Swimlane className={styles.container}>
      <Swimlane.Header>
        <Swimlane.Title>{view.label}</Swimlane.Title>
        <Swimlane.Buttons />
      </Swimlane.Header>
      <Swimlane.Slider>
        {data.map((item: Layout.SectionItem) => (
          <ContinueWatchingItem key={createKey(item)} pattern={view.style.pattern} item={item} />
        ))}
      </Swimlane.Slider>
    </Swimlane>
  );
}

interface ContinueWatchingItemProps {
  item: Layout.SectionItem;
  pattern: Layout.SectionView['style']['pattern'];
}

function ContinueWatchingItem({ item, pattern }: ContinueWatchingItemProps) {
  if (item.type === 'title') {
    return <ContinueWatchingTitle item={item} pattern={pattern} />;
  }

  return <PatternHandler key={createKey(item)} pattern={pattern} item={item} />;
}

interface ContinueWatchingTitleProps {
  item: Layout.SectionItemTitle;
  pattern: Layout.SectionView['style']['pattern'];
}

function ContinueWatchingTitle({ item, pattern }: ContinueWatchingTitleProps) {
  const { data: bookmark } = useMostRecentBookmark(item.title.masterId);

  if (bookmark !== undefined) {
    return (
      <PatternHandler
        key={createKey(item)}
        pattern={pattern}
        item={item}
        params={{ platformTitleId: bookmark.platformTitleId }}
        overwrites={{
          linkId: item.title.masterId,
        }}
      />
    );
  }

  return (
    <PatternHandler
      key={createKey(item)}
      pattern={pattern}
      item={item}
      overwrites={{
        linkId: item.title.masterId,
      }}
    />
  );
}
